/**
 * Acceptance Report API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  Acceptance,
  AcceptanceDetail,
  AcceptanceState,
  AcceptanceCreateRequest,
  AcceptanceUpdateRequest,
  AcceptanceSignInfo,
  AcceptanceTemplate,
  CompletedOrder,
  CompletedOrderDetail,
  PaymentRequestData,
} from '../../types';

const API_PREFIX = '/api/saiens/acceptance';

interface CompletedOrdersParams {
  offset?: number;
  limit?: number;
  search?: string;
  my_orders?: boolean;
  exclude_invoiced?: boolean;
  has_completion_date?: boolean;
}

interface AcceptanceListParams {
  offset?: number;
  limit?: number;
  state?: string;
  search?: string;
  my_orders?: boolean;
  unpaid_only?: boolean;
  paid_only?: boolean;
  invoiced_done?: boolean;
}

/**
 * Get active acceptance templates
 */
export async function getTemplates(): Promise<ApiResponse<AcceptanceTemplate[]>> {
  const response = await apiClient.get<ApiResponse<AcceptanceTemplate[]>>(
    `${API_PREFIX}/templates`,
  );
  return response.data;
}

/**
 * Get completed orders available for acceptance
 */
export async function getCompletedOrders(
  params: CompletedOrdersParams = {},
): Promise<ApiResponse<PaginatedData<CompletedOrder>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<CompletedOrder>>>(
    `${API_PREFIX}/completed-orders`,
    { params },
  );
  return response.data;
}

/**
 * Get completed order detail with construction photos
 */
export async function getCompletedOrderDetail(
  orderId: number,
): Promise<ApiResponse<CompletedOrderDetail>> {
  const response = await apiClient.get<ApiResponse<CompletedOrderDetail>>(
    `${API_PREFIX}/completed-orders/${orderId}`,
  );
  return response.data;
}

/**
 * Create acceptance report
 */
export async function createAcceptance(
  data: AcceptanceCreateRequest,
): Promise<ApiResponse<AcceptanceDetail>> {
  const response = await apiClient.post<ApiResponse<AcceptanceDetail>>(
    `${API_PREFIX}/create`,
    data,
  );
  return response.data;
}

/**
 * List acceptance reports
 */
export async function getAcceptanceList(
  params: AcceptanceListParams = {},
): Promise<ApiResponse<PaginatedData<Acceptance>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<Acceptance>>>(
    `${API_PREFIX}/list`,
    { params },
  );
  return response.data;
}

/**
 * Get acceptance detail
 */
export async function getAcceptance(
  id: number,
): Promise<ApiResponse<AcceptanceDetail>> {
  const response = await apiClient.get<ApiResponse<AcceptanceDetail>>(
    `${API_PREFIX}/${id}`,
  );
  return response.data;
}

/**
 * Update acceptance
 */
export async function updateAcceptance(
  id: number,
  data: AcceptanceUpdateRequest,
): Promise<ApiResponse<AcceptanceDetail>> {
  const response = await apiClient.put<ApiResponse<AcceptanceDetail>>(
    `${API_PREFIX}/${id}`,
    data,
  );
  return response.data;
}

/**
 * Employee sign acceptance.
 * 業務單簽新流程：簽名完成後 state 直接變 'signed'（驗收完成），不再有等客戶簽名的中間狀態。
 */
export async function employeeSign(
  id: number,
  signature: string,
): Promise<
  ApiResponse<{
    id: number;
    access_token: string;
    employee_signed_date: string;
    state: AcceptanceState;
  }>
> {
  const response = await apiClient.post<
    ApiResponse<{
      id: number;
      access_token: string;
      employee_signed_date: string;
      state: AcceptanceState;
    }>
  >(`${API_PREFIX}/${id}/employee-sign`, { signature });
  return response.data;
}

/**
 * Get customer sign info (public, token-based; 檢視用)
 */
export async function getSignInfo(
  token: string,
): Promise<ApiResponse<AcceptanceSignInfo>> {
  const response = await apiClient.get<ApiResponse<AcceptanceSignInfo>>(
    `${API_PREFIX}/sign/${token}`,
  );
  return response.data;
}

/**
 * Submit acceptance (create invoice)
 */
export async function submitAcceptance(
  id: number,
): Promise<ApiResponse<AcceptanceDetail>> {
  const response = await apiClient.post<ApiResponse<AcceptanceDetail>>(
    `${API_PREFIX}/${id}/submit`,
  );
  return response.data;
}

/**
 * Issue e-invoice for existing invoice, returns PDF URL
 */
export async function issueAcceptanceEinvoice(
  id: number,
): Promise<ApiResponse<AcceptanceDetail & { einvoice_pdf_url?: string | null }>> {
  const response = await apiClient.post<
    ApiResponse<AcceptanceDetail & { einvoice_pdf_url?: string | null }>
  >(`${API_PREFIX}/${id}/issue-einvoice`);
  return response.data;
}

/**
 * Get payment request data for frontend PDF generation
 */
export async function getPaymentData(id: number): Promise<PaymentRequestData> {
  const response = await apiClient.get<ApiResponse<PaymentRequestData>>(
    `${API_PREFIX}/${id}/payment-data`,
  );
  if (!response.data.data) {
    throw new Error('No payment data returned');
  }
  return response.data.data;
}

/**
 * Upload photos for acceptance (linked to sale.order)
 */
export async function uploadAcceptancePhotos(
  saleOrderId: number,
  images: string[],
): Promise<ApiResponse<{ id: number; name: string; url: string; mimetype: string; file_size: number }[]>> {
  const response = await apiClient.post<
    ApiResponse<{ id: number; name: string; url: string; mimetype: string; file_size: number }[]>
  >(`${API_PREFIX}/upload-photos`, {
    sale_order_id: saleOrderId,
    images,
  });
  return response.data;
}

/**
 * Download acceptance PDF (authenticated fetch → blob download)
 */
export async function downloadAcceptancePdf(id: number): Promise<void> {
  const response = await apiClient.get(`${API_PREFIX}/${id}/pdf`, {
    responseType: 'blob',
  });
  _openPdfInNewTab(response.data);
}

/**
 * Open package PDF via token URL (LIFF-compatible, no blob download).
 * Falls back to blob download if link generation fails.
 */
export async function downloadPackagePdf(id: number): Promise<void> {
  try {
    const response = await apiClient.get<ApiResponse<{ pdf_url: string }>>(
      `${API_PREFIX}/${id}/package-pdf-link`,
    );
    const pdfUrl = response.data?.data?.pdf_url;
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
      return;
    }
  } catch {
    // Link generation failed, fall back to blob download
  }
  const blobResponse = await apiClient.get(`${API_PREFIX}/${id}/package-pdf`, {
    responseType: 'blob',
  });
  _openPdfInNewTab(blobResponse.data);
}

/**
 * Download e-invoice PDF
 */
export async function downloadEinvoicePdf(id: number): Promise<void> {
  const response = await apiClient.get(`${API_PREFIX}/${id}/einvoice-pdf`, {
    responseType: 'blob',
  });
  _openPdfInNewTab(response.data);
}

/**
 * Send LINE notification (Flex Message with package + einvoice download links)
 * to the customer's bound LINE group/user.
 *
 * Backend: POST /api/saiens/acceptance/{id}/notify-line
 * Requires: state in (invoiced, done) + partner.line_group_id|line_id +
 *           invoice posted + einvoice issued
 */
export async function notifyLineCustomer(
  id: number,
): Promise<ApiResponse<{ acceptance_id: number; target: 'group' | 'user'; partner_name: string }>> {
  const response = await apiClient.post<
    ApiResponse<{ acceptance_id: number; target: 'group' | 'user'; partner_name: string }>
  >(`${API_PREFIX}/${id}/notify-line`);
  return response.data;
}

/**
 * Request payment delay for an acceptance
 */
export async function requestPaymentDelay(
  id: number,
  reason: string,
  delayDays: number,
): Promise<ApiResponse<void>> {
  const response = await apiClient.post<ApiResponse<void>>(
    `${API_PREFIX}/${id}/request-payment-delay`,
    { reason, delay_days: delayDays },
  );
  return response.data;
}

function _openPdfInNewTab(blob: Blob): void {
  const pdfBlob = new Blob([blob], { type: 'application/pdf' });
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
}
